import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  FileUp,
  X,
  Search,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { createPortal } from "react-dom";
import axios from "axios";
import "./index.css";

const buttonClass =
  "bg-default-500 text-white border-none py-2 px-4 rounded font-medium cursor-pointer transition-colors duration-200 hover:bg-default-600";
const inlineButtonClass =
  "text-default-500 bg-white border text-sm py-2 px-4 rounded-lg font-medium cursor-pointer transition-colors duration-200 hover:bg-default-50 inline-block";

function Spinner({ size = "default", variant = "primary" }) {
  const sizeClasses = {
    xs: "w-3 h-3 border-[1.5px]",
    sm: "w-4 h-4 border-2",
    default: "w-5 h-5 border-2",
    lg: "w-6 h-6 border-2",
    xl: "w-8 h-8 border-[3px]",
  };
  const variantClasses = {
    white: "border-white/50 border-t-white",
    default: "border-gray-500/50 border-t-gray-500",
    primary: "border-default-500/50 border-t-default-500",
  };
  return (
    <span
      className={`inline-block rounded-full animate-spin ${sizeClasses[size]} ${variantClasses[variant]}`}
    />
  );
}

function PixabayDrawer({ isOpen, onClose, onSelectionDone }) {
  const [isLocalOpen, setIsLocalOpen] = useState(false);
  const portalRoot = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImages, setSelectedImages] = useState({});
  const [error, setError] = useState(null);
  const [showSelectionPreview, setShowSelectionPreview] = useState(false);

  // Create portal element
  useEffect(() => {
    portalRoot.current = document.createElement("div");
    portalRoot.current.id = "pixabay-drawer-portal";
    document.body.appendChild(portalRoot.current);

    return () => {
      if (portalRoot.current) {
        document.body.removeChild(portalRoot.current);
      }
    };
  }, []);

  // Handle drawer opening/closing
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsLocalOpen(true);
      }, 50);
    } else {
      setIsLocalOpen(false);
    }

    // Add escape key handler
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen]);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch search results when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim().length > 0) {
      fetchImages();
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm]);

  const fetchImages = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/search/remote/pixabay?q=${encodeURIComponent(debouncedSearchTerm)}`,
      );

      setSearchResults(response.data.hits || []);
    } catch (err) {
      console.error("Error fetching images from Pixabay:", err);
      setError(
        "Une erreur s'est produite lors de la recherche d'images. Veuillez réessayer.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClose = () => {
    setIsLocalOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const toggleImageSelection = (image) => {
    setSelectedImages((prev) => {
      const newSelectedImages = { ...prev };

      if (prev[image.id]) {
        delete newSelectedImages[image.id];
      } else {
        newSelectedImages[image.id] = image;
      }

      return newSelectedImages;
    });
  };

  const handleUploadSelection = () => {
    const selectedImagesArray = Object.values(selectedImages);
    onSelectionDone(selectedImagesArray);
    handleClose();
  };

  const selectedImagesCount = Object.keys(selectedImages).length;
  const transitionClasses = "transition-transform duration-300";

  const toggleSelectionPreview = () => {
    setShowSelectionPreview(!showSelectionPreview);
  };

  const removeSelectedImage = (imageId) => {
    setSelectedImages((prev) => {
      const newSelectedImages = { ...prev };
      delete newSelectedImages[imageId];
      return newSelectedImages;
    });
  };

  const drawerContent = (
    <>
      <button
        type="button"
        onClick={handleClose}
        style={{
          opacity: isLocalOpen ? 0.8 : 0,
        }}
        className={`fixed top-0 left-0 right-0 bottom-0 bg-gray-700/80 z-[99] ${transitionClasses}`}
      ></button>
      <div
        style={{
          transform: `translateX(${isLocalOpen ? "0" : "100%"})`,
        }}
        className={`fixed w-screen lg:w-[700px] top-0 right-0 bottom-0 bg-white z-[100] flex flex-col divide-y divide-gray-300 border-l border-gray-300 ${transitionClasses}`}
      >
        <div className="flex-shrink-0 flex items-center justify-between py-3 px-6">
          <h2 className="font-medium text-xl tracking-tight">
            Rechercher des images sur Pixabay
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-shrink-0 p-4 bg-white border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Rechercher des images..."
              className="w-full py-2 px-4 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          {selectedImagesCount > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={toggleSelectionPreview}
                className="flex items-center text-sm text-default-600 hover:text-default-700 font-medium"
              >
                {selectedImagesCount} image{selectedImagesCount > 1 ? "s" : ""}{" "}
                sélectionnée{selectedImagesCount > 1 ? "s" : ""}
                {showSelectionPreview ? (
                  <ChevronUp size={16} className="ml-1" />
                ) : (
                  <ChevronDown size={16} className="ml-1" />
                )}
              </button>

              {showSelectionPreview && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Images sélectionnées:
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.values(selectedImages).map((image) => (
                      <div
                        key={image.id}
                        className="relative group aspect-square"
                      >
                        <img
                          src={image.previewURL}
                          alt={image.tags}
                          className="w-full h-full object-cover rounded"
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedImage(image.id);
                          }}
                        >
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {isLoading && (
            <div className="flex justify-center items-center h-32">
              <Spinner size="lg" variant="primary" />
            </div>
          )}

          {error && <div className="text-red-500 text-center p-4">{error}</div>}

          {!isLoading &&
            !error &&
            searchResults.length === 0 &&
            debouncedSearchTerm && (
              <div className="text-center text-gray-500 py-12">
                Aucun résultat trouvé pour "{debouncedSearchTerm}"
              </div>
            )}

          {!isLoading &&
            !error &&
            searchResults.length === 0 &&
            !debouncedSearchTerm && (
              <div className="text-center text-gray-500 py-12">
                Entrez un terme de recherche pour trouver des images
              </div>
            )}

          {!isLoading && searchResults.length > 0 && (
            <div className="grid grid-cols-3 gap-4">
              {searchResults.map((image) => (
                <div
                  key={image.id}
                  className={`relative rounded-lg overflow-hidden cursor-pointer transition-all group hover:shadow-md ${
                    selectedImages[image.id] ? "ring-2 ring-default-500" : ""
                  }`}
                  onClick={() => toggleImageSelection(image)}
                >
                  <img
                    src={image.webformatURL}
                    alt={image.tags}
                    className="w-full h-40 object-cover"
                  />

                  <div className="absolute inset-0 flex items-center justify-center">
                    {selectedImages[image.id] && (
                      <div className="absolute top-2 right-2 bg-default-500 rounded-full p-1">
                        <Check size={16} className="text-white" />
                      </div>
                    )}
                    <div
                      className={`
                      absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity
                      ${selectedImages[image.id] ? "opacity-20" : ""}
                    `}
                    ></div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                    {image.user}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedImagesCount > 0 ? (
              <button
                type="button"
                onClick={toggleSelectionPreview}
                className="flex items-center text-default-600 hover:text-default-700 font-medium"
              >
                <span>
                  {selectedImagesCount} image
                  {selectedImagesCount > 1 ? "s" : ""} sélectionnée
                  {selectedImagesCount > 1 ? "s" : ""}
                </span>
                {showSelectionPreview ? (
                  <ChevronUp size={16} className="ml-1" />
                ) : (
                  <ChevronDown size={16} className="ml-1" />
                )}
              </button>
            ) : (
              <span>Sélectionnez des images à utiliser</span>
            )}
          </div>

          <button
            type="button"
            className={`${buttonClass} ${
              selectedImagesCount === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={selectedImagesCount === 0}
            onClick={handleUploadSelection}
          >
            Utiliser la sélection
          </button>
        </div>
      </div>
    </>
  );

  if (!portalRoot.current || !isOpen) return null;

  return createPortal(drawerContent, portalRoot.current);
}

function Component({ currentSignedIds, fieldName, mode, autosubmit, labelPlural = "visuels", pixabayEnabled = true, accept = "image/*" }) {
  const [files, setFiles] = useState([]);
  const [pixabayDrawer, setPixabayDrawer] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles.length) return;

    // Create preview URLs for each file
    const newFiles = Array.from(selectedFiles).map((file) => ({
      id: Math.random().toString(36).substring(2, 11),
      file,
      preview: URL.createObjectURL(file),
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    if (autosubmit) {
      setIsUploading(true);
      autosubmit();
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      files.forEach((fileObj) => {
        if (fileObj.preview) URL.revokeObjectURL(fileObj.preview);
      });
    };
  }, [files]);

  const removeFile = (fileToRemove) => {
    // Remove from UI state
    setFiles(files.filter((fileObj) => fileObj.id !== fileToRemove.id));

    // Also remove from the actual file input
    if (fileInputRef.current) {
      const dt = new DataTransfer();

      // Add all files except the one being removed
      Array.from(fileInputRef.current.files).forEach((file) => {
        // Compare file objects to find the one to remove
        if (file !== fileToRemove.file) {
          dt.items.add(file);
        }
      });

      // Update the file input's files collection
      fileInputRef.current.files = dt.files;
    }

    // Release the object URL
    if (fileToRemove.preview) URL.revokeObjectURL(fileToRemove.preview);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleClosePixabay = () => {
    setPixabayDrawer(false);
  };

  const handlePixabaySelection = async (selectedImages) => {
    // For each selected image, fetch it and create a File object
    const fetchPromises = selectedImages.map(async (image) => {
      try {
        // Fetch the image
        const response = await fetch(image.webformatURL);
        const blob = await response.blob();

        // Extract file extension from URL
        const extension = image.webformatURL.split(".").pop().split("?")[0];

        // Create a File object
        const file = new File([blob], `pixabay-${image.id}.${extension}`, {
          type: `image/${extension}`,
        });

        // Create and return a file object with preview
        return {
          id: `pixabay-${image.id}`,
          file,
          preview: URL.createObjectURL(blob),
          source: "pixabay",
        };
      } catch (error) {
        console.error("Error fetching image:", error);
        return null;
      }
    });

    // Wait for all images to be fetched
    const fetchedFiles = await Promise.all(fetchPromises);

    // Filter out any null values from errors
    const validFiles = fetchedFiles.filter(Boolean);

    // Add these files to our state
    setFiles((prev) => [...prev, ...validFiles]);

    // Also add these files to the file input's files property
    if (fileInputRef.current && validFiles.length > 0) {
      // Create a DataTransfer object
      const dataTransfer = new DataTransfer();

      // First, add all existing files from the file input
      if (fileInputRef.current.files.length > 0) {
        Array.from(fileInputRef.current.files).forEach((file) => {
          dataTransfer.items.add(file);
        });
      }

      // Then add all the new Pixabay files
      validFiles.forEach((fileObj) => {
        dataTransfer.items.add(fileObj.file);
      });

      // Update the file input's files property
      fileInputRef.current.files = dataTransfer.files;
    }

    if (autosubmit) {
      setIsUploading(true);
      autosubmit();
    }
  };

  return (
    <>
      {/* Hidden actual file input */}
      <input
        ref={fileInputRef}
        type="file"
        name={`${fieldName}[]`}
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept={accept}
      />

      {/* Dropzone-styled button */}
      {mode === "inline" ? (
        <div className="flex items-center justify-center flex-col space-y-4">
          {isUploading ? (
            <div className="flex items-center justify-center flex-col space-y-2 py-4">
              <Spinner size="lg" variant="primary" />
              <span className="text-sm text-default-600">Téléchargement en cours...</span>
            </div>
          ) : (
            <>
              <div onClick={triggerFileInput}>
                <button type="button" className={inlineButtonClass}>
                  {`Ajouter les ${labelPlural}`}
                </button>
              </div>
              {pixabayEnabled && (
                <button
                  type="button"
                  className="text-xs text-default-600 hover:underline"
                  onClick={() => setPixabayDrawer(!pixabayDrawer)}
                >
                  Rechercher sur pixabay
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div className="w-full mb-4">
            <div
              onClick={triggerFileInput}
              className="group border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer bg-gray-50 transition-all duration-300 hover:border-default-200 hover:bg-default-50"
            >
              <div className="flex flex-col items-center gap-4">
                <div className="flex justify-center items-center">
                  <FileUp
                    size={48}
                    className="text-gray-500 opacity-80 transition-all duration-300 group-hover:text-default-600"
                  />
                </div>
                <p className="text-gray-600 m-0 group-hover:text-default-600">
                  {`Cliquer pour ajouter des ${labelPlural}`}
                </p>
                <button type="button" className={buttonClass}>
                  {`Ajouter les ${labelPlural}`}
                </button>
              </div>
            </div>

            <div className="mt-4 text-center text-xs">
              {pixabayEnabled && (
                <button
                  type="button"
                  className="text-default-600 hover:underline"
                  onClick={() => setPixabayDrawer(!pixabayDrawer)}
                >
                  Rechercher sur pixabay
                </button>
              )}
            </div>
          </div>

          {/* File previews */}
          {files.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              {files.map((fileObj) => (
                <div
                  key={fileObj.id}
                  className="flex items-center p-2 border border-gray-200 rounded bg-white"
                >
                  {fileObj.file.type.startsWith("image/") ? (
                    <img
                      src={fileObj.preview}
                      alt={fileObj.file.name}
                      className="w-10 h-10 object-cover rounded mr-4"
                    />
                  ) : (
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded mr-4 text-gray-600 text-xs uppercase font-medium">
                      {fileObj.file.type === "application/pdf" ? "PDF" : fileObj.file.name.split(".").pop()}
                    </div>
                  )}
                  <div className="flex-1 flex flex-col">
                    <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]">
                      {fileObj.file.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {(fileObj.file.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className="bg-transparent border-none text-gray-500 cursor-pointer hover:text-red-500 flex items-center justify-center"
                    onClick={() => removeFile(fileObj)}
                  >
                    <X size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Hidden inputs for current signed IDs */}
      {currentSignedIds.map((id) => (
        <input key={id} type="hidden" name={`${fieldName}[]`} value={id} />
      ))}

      {/* Pixabay Drawer */}
      {pixabayEnabled && (
        <PixabayDrawer
          isOpen={pixabayDrawer}
          onClose={handleClosePixabay}
          onSelectionDone={handlePixabaySelection}
        />
      )}
    </>
  );
}

export default class extends Controller {
  connect() {
    const fieldName = this.element.dataset.fieldName;
    const mode = this.element.dataset.mode;
    const autosubmit = this.element.dataset.autosubmit === "true" ? true : false;
    const currentSignedIds = JSON.parse(this.element.dataset.currentSignedIds || "[]");
    // New: customization hooks
    const labelPlural = this.element.dataset.labelPlural || "images";
    const pixabayEnabled = (this.element.dataset.pixabayEnabled || "true") === "true";
    const accept = this.element.dataset.accept || "image/*";

    const autosubmitFunction = autosubmit ? () => {
      this.element.closest("form").requestSubmit();
    } : null;

    this.root = createRoot(this.element);
    this.root.render(
      <Component
        mode={mode}
        autosubmit={autosubmitFunction}
        currentSignedIds={currentSignedIds}
        fieldName={fieldName}
        labelPlural={labelPlural}
        pixabayEnabled={pixabayEnabled}
        accept={accept}
      />,
    );
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
    }
  }
}
